<template>
  <v-card class="my-4">
    <div class="d-flex flex-wrap align-center px-4 pt-3 pb-1 ed-title">
      <v-btn
        icon
        class="mr-1"
        title="All emails"
        :to="{ name: 'emails' }"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h6 mr-3">
        {{ email.name || 'Email' }}
      </h1>
      <v-chip
        v-if="email.key && !email.version"
        small
        color="warning"
        text-color="white"
      >
        Draft
      </v-chip>
      <v-chip
        v-else-if="email.hasDraft || dirty"
        small
        color="warning"
        outlined
      >
        Unpublished changes
      </v-chip>
      <v-spacer />
      <v-btn
        v-if="!isLayout"
        outlined
        class="mr-2"
        :loading="sendingTest"
        :disabled="!ready || hasProblems"
        @click="sendTest"
      >
        <v-icon left>
          mdi-send-outline
        </v-icon>
        Send me a test
      </v-btn>
      <v-btn
        text
        class="mr-2"
        :loading="saving"
        :disabled="!dirty"
        @click="saveDraft"
      >
        Save draft
      </v-btn>
      <v-btn
        color="primary"
        :loading="publishing"
        :disabled="!canPublish"
        @click="publish"
      >
        Publish
      </v-btn>
    </div>
    <div class="px-4 pb-3 text-body-2 grey--text text--darken-1 ed-sub">
      {{ email.sentWhen }}
      <span v-if="email.key"> · {{ versionText }}</span>
      <template v-if="email.history && email.history.length">
        ·
        <a
          href="#"
          @click.prevent="historyDialog = true"
        >History</a>
      </template>
    </div>

    <v-divider />

    <v-row
      no-gutters
      class="ed-body"
    >
      <v-col
        cols="12"
        md="5"
        class="pa-4 ed-form"
      >
        <v-alert
          v-if="hasProblems"
          type="error"
          text
          dense
        >
          <div class="font-weight-medium">
            Fix this before publishing:
          </div>
          <div
            v-for="(p, i) in preview.problems"
            :key="i"
          >
            {{ p }}
          </div>
        </v-alert>
        <v-alert
          v-if="email.kind === 'transactional' && !email.version"
          type="info"
          text
          dense
        >
          A developer connects new emails to the event that sends them. Until then, publishing makes it ready but nobody
          receives it.
        </v-alert>

        <template v-if="!isLayout">
          <v-text-field
            v-model="subject"
            label="Subject"
            outlined
            dense
          />
          <v-text-field
            v-model="preheader"
            label="Preview text"
            hint="Shown after the subject in the inbox."
            persistent-hint
            outlined
            dense
            class="mb-3"
          />
        </template>

        <div class="text-body-2 font-weight-medium mb-1">
          {{ isLayout ? 'Header and footer' : 'Body' }}
        </div>
        <v-textarea
          ref="body"
          v-model="body"
          outlined
          rows="20"
          auto-grow
          spellcheck="false"
          hide-details
          class="body-input"
          @blur="rememberCaret"
        />

        <div class="mt-5">
          <div class="text-body-2 font-weight-medium">
            Fields
          </div>
          <div class="text-caption grey--text text--darken-1 mb-2">
            {{ fieldsHint }}
          </div>
          <v-simple-table
            dense
            class="fields-table"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type this</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="f in fields"
                :key="f.name"
                class="cursor-pointer"
                :title="`Put ${tagFor(f.name)} where your cursor was`"
                @click="insertField(f.name)"
              >
                <td class="text-no-wrap">
                  <v-icon
                    v-if="isUsed(f.name)"
                    small
                    color="success"
                    class="mr-1"
                  >
                    mdi-check
                  </v-icon>
                  {{ f.label || f.name }}
                </td>
                <td class="field-tag">
                  {{ tagFor(f.name) }}
                </td>
                <td class="field-example">
                  {{ f.example }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-col>

      <v-col
        cols="12"
        md="7"
        class="pa-4 ed-preview"
      >
        <div class="d-flex flex-wrap align-center mb-3 pv-bar">
          <v-btn-toggle
            v-model="device"
            mandatory
            dense
            class="mr-3"
          >
            <v-btn value="desktop">
              Desktop
            </v-btn>
            <v-btn value="phone">
              Phone
            </v-btn>
          </v-btn-toggle>
          <v-select
            v-model="brand"
            :items="brands"
            dense
            outlined
            hide-details
            class="mr-3 pv-select"
          />
          <v-checkbox
            v-if="!isLayout"
            v-model="noFirstName"
            label="No first name"
            dense
            hide-details
            class="mt-0"
          />
        </div>
        <div
          v-if="!isLayout"
          class="inbox mb-3"
        >
          <div class="text-caption grey--text text--darken-1">
            {{ fromLine }}
          </div>
          <div class="font-weight-medium">
            {{ preview.subject || 'No subject' }}
          </div>
          <div class="grey--text text-truncate">
            {{ preview.preheader || ' ' }}
          </div>
        </div>
        <div
          class="frame-wrap"
          :class="{ phone: device === 'phone' }"
        >
          <iframe
            title="Email preview"
            sandbox=""
            :srcdoc="preview.html || ''"
          />
        </div>
      </v-col>
    </v-row>

    <v-dialog
      v-model="historyDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title>History</v-card-title>
        <v-card-text>
          <div
            v-for="v in email.history"
            :key="v.version"
            class="d-flex align-center py-2 history-row"
          >
            <div class="flex-grow-1">
              <span class="font-weight-medium">Version {{ v.version }}</span>
              <v-chip
                v-if="v.isLive"
                x-small
                color="success"
                text-color="white"
                class="ml-2"
              >
                Live
              </v-chip>
              <div class="text-caption">
                {{ v.publishedBy }} · {{ formatDate(v.publishedAt) }}<span v-if="v.note"> · {{ v.note }}</span>
              </div>
            </div>
            <v-btn
              v-if="!v.isLive"
              text
              small
              @click="restore(v.version)"
            >
              Restore
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="historyDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';

const PREVIEW_DELAY_MS = 350;

export default {
  name: 'EmailDetail',
  // Unsaved words would be lost on the way out: ask first (there is no autosave, by choice).
  async beforeRouteLeave(to, from, next) {
    if (!this.dirty) return next();
    const leave = await this.$root.$confirm(
      'Leave without saving?',
      'Your changes to this email are not saved. Save a draft first to keep them.',
      { color: 'primary' }
    );
    next(leave);
  },
  data: () => ({
    ready: false,
    subject: '',
    preheader: '',
    body: '',
    saved: { subject: '', preheader: '', body: '' },
    preview: { subject: '', preheader: '', html: '', problems: [] },
    device: 'desktop',
    brand: 'mcs',
    noFirstName: false,
    brands: [
      { text: 'MyCreativeShop', value: 'mcs' },
      { text: 'A white-label brand', value: 'wl' }
    ],
    caret: null,
    previewTimer: null,
    saving: false,
    publishing: false,
    sendingTest: false,
    historyDialog: false
  }),
  computed: {
    ...mapGetters({ email: 'emails/email' }),
    key() {
      return this.$route.params.key;
    },
    isLayout() {
      return this.email.kind === 'layout';
    },
    // The fields come from the code that sends the email (EmailService.MessageFields), so this is exactly what the
    // body can use.
    fields() {
      return this.email.fields || [];
    },
    fieldsHint() {
      if (this.email.kind === 'transactional' && !this.email.version && this.fields.every((f) => this.isBrandField(f.name))) {
        return 'Only the brand and person fields until a developer connects this email to the event that sends it. Click a field to put it where your cursor was.';
      }
      return 'Everything this email can use. Click a field to put it where your cursor was; a check means the email already uses it.';
    },
    allWords() {
      return `${this.subject}\n${this.preheader}\n${this.body}`;
    },
    dirty() {
      return (
        this.ready &&
        (this.subject !== this.saved.subject || this.preheader !== this.saved.preheader || this.body !== this.saved.body)
      );
    },
    hasProblems() {
      return !!(this.preview.problems && this.preview.problems.length);
    },
    canPublish() {
      return this.ready && !this.hasProblems && (this.dirty || this.email.hasDraft || !this.email.version);
    },
    versionText() {
      if (!this.email.version) return 'Not published yet. Nobody receives it.';
      return `Version ${this.email.version}, published ${this.formatDate(this.email.publishedAt)}`;
    },
    fromLine() {
      const name = this.brand === 'wl' ? 'Acme Print Co.' : 'MyCreativeShop';
      const marketing = this.email.kind === 'automation' || this.email.kind === 'broadcast';
      const address = marketing ? 'mail@news.mycreativeshop.com' : 'mail@mycreativeshop.com';
      return `${name} <${address}>`;
    }
  },
  watch: {
    subject: 'queuePreview',
    preheader: 'queuePreview',
    body: 'queuePreview',
    brand: 'queuePreview',
    noFirstName: 'queuePreview'
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.previewTimer);
  },
  methods: {
    load() {
      this.ready = false;
      this.$store
        .dispatch('emails/get', this.key)
        .then((detail) => this.takeWords(detail))
        .catch((error) => this.$root.$snackbarError(this.errorText(error, 'Could not load this email.')));
    },
    // The words the editor starts from (the draft when there is one) become the "saved" baseline for dirty-checking.
    takeWords(detail) {
      this.subject = detail.subject || '';
      this.preheader = detail.preheader || '';
      this.body = detail.body || '';
      this.saved = { subject: this.subject, preheader: this.preheader, body: this.body };
      this.ready = true;
      this.refreshPreview();
    },
    words() {
      return { key: this.key, subject: this.subject, preheader: this.preheader, body: this.body };
    },
    queuePreview() {
      if (!this.ready) return;
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(this.refreshPreview, PREVIEW_DELAY_MS);
    },
    refreshPreview() {
      this.$store
        .dispatch('emails/preview', { ...this.words(), whiteLabel: this.brand === 'wl', noFirstName: this.noFirstName })
        .then((preview) => {
          this.preview = preview;
        })
        .catch((error) => this.$root.$snackbarError(this.errorText(error, 'Could not show the preview.')));
    },
    rememberCaret() {
      const el = this.$refs.body && this.$refs.body.$refs.input;
      this.caret = el ? el.selectionStart : null;
    },
    tagFor(name) {
      return `{{ ${name} }}`;
    },
    isBrandField(name) {
      return /^(trigger\.master(Name|LogoUrl|Color)|customer\.(email|first_name))$/.test(name);
    },
    // Used in an output ({{ name ... }}) or a condition ({% if name %}).
    isUsed(name) {
      const n = name.replace(/\./g, '\\.');
      return new RegExp(`\\{\\{-?\\s*${n}(?![\\w.])|\\{%-?\\s*(if|unless|elsif)\\s+${n}(?![\\w.])`).test(this.allWords);
    },
    insertField(name) {
      const tag = this.tagFor(name);
      const pos = this.caret == null ? this.body.length : this.caret;
      this.body = this.body.slice(0, pos) + tag + this.body.slice(pos);
      this.$nextTick(() => {
        const el = this.$refs.body && this.$refs.body.$refs.input;
        if (!el) return;
        el.focus();
        el.setSelectionRange(pos + tag.length, pos + tag.length);
      });
    },
    saveDraft() {
      this.saving = true;
      this.$store
        .dispatch('emails/saveDraft', this.words())
        .then((detail) => {
          this.takeWords(detail);
          this.$root.$snackbarSuccess(
            this.email.version ? `Draft saved. Customers still get version ${this.email.version} until you publish.` : 'Draft saved.'
          );
        })
        .catch((error) => this.$root.$snackbarError(this.errorText(error, 'The draft was not saved.')))
        .finally(() => {
          this.saving = false;
        });
    },
    publish() {
      this.publishing = true;
      this.$store
        .dispatch('emails/publish', this.words())
        .then((result) => {
          this.takeWords(result.detail);
          this.$root.$snackbarSuccess(
            this.isLayout
              ? `Published. Every email now uses version ${result.version}.`
              : `Published. The next "${this.email.name}" email uses version ${result.version}.`
          );
        })
        .catch((error) => this.$root.$snackbarError(this.errorText(error, 'It was not published.')))
        .finally(() => {
          this.publishing = false;
        });
    },
    sendTest() {
      this.sendingTest = true;
      this.$store
        .dispatch('emails/sendTest', this.words())
        .then((result) => this.$root.$snackbarSuccess(`Test sent to ${result.email}.`))
        .catch((error) => this.$root.$snackbarError(this.errorText(error, 'The test was not sent.')))
        .finally(() => {
          this.sendingTest = false;
        });
    },
    async restore(version) {
      if (this.dirty) {
        const go = await this.$root.$confirm(
          'Replace your changes?',
          `Restoring version ${version} replaces the words you haven't saved.`,
          { color: 'primary' }
        );
        if (!go) return;
      }
      this.$store
        .dispatch('emails/restore', { key: this.key, version })
        .then((detail) => {
          this.takeWords(detail);
          this.historyDialog = false;
          this.$root.$snackbarSuccess(`Version ${version} is in your draft. Publish to make it live.`);
        })
        .catch((error) => this.$root.$snackbarError(this.errorText(error, 'That version was not restored.')));
    },
    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    errorText(error, fallback) {
      const data = error && error.response && error.response.data;
      if (typeof data === 'string') return data;
      return (data && data.message) || fallback;
    }
  }
};
</script>

<style lang="scss" scoped>
.ed-form {
  border-right: 1px solid #e5e5e5;
}

.ed-preview {
  background: #fafafa;
}

.body-input ::v-deep textarea {
  font-family: ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', monospace;
  font-size: 12.5px;
  line-height: 1.6;
}

.pv-select {
  max-width: 220px;
}

.inbox {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 10px 12px;
}

.frame-wrap {
  display: flex;
  justify-content: center;

  iframe {
    width: 100%;
    max-width: 640px;
    height: 680px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    background: #fff;
    transition: max-width 0.2s;
  }

  &.phone iframe {
    max-width: 375px;
  }
}

.fields-table {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;

  .field-tag {
    font-family: ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', monospace;
    font-size: 12px;
    white-space: nowrap;
  }

  .field-example {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #757575;
  }

  tbody tr:hover td {
    background: #f5f5f5;
  }
}

.history-row + .history-row {
  border-top: 1px solid #e5e5e5;
}

@media (max-width: 959px) {
  .ed-form {
    border-right: 0;
    border-bottom: 1px solid #e5e5e5;
  }
}
</style>
